import { Title, Container, Paragraph, Card, Button, Icon } from '@/components'
import { getAreaInfoById, getSubscriptionsByAreaId } from '@/store/slices/stagesSlice/stagesSlice'
import Link from 'next/link'
import getLinkHref from '@/helpers/getLinkHref'
import { useSelector } from 'react-redux'
import { RootState } from '@/store'
import { IAreaSubscription } from '@/store/slices/stagesSlice/interfaces'
import formatNumberToString from '@/helpers/formatNumberToString'
import unicode from '@/helpers/unicodeChars'
import { useAreaStyles } from '@/helpers/hooks/useAreaStyles'
import getSubscriptionInterval from '@/helpers/getSubscriptionInterval'
import styles from './SubscriptionsPage.module.scss'
import { WeekDaysEnum } from '../SubscriptionFieldsPage/SubscriptionPageInfo'
import { formatWeekDays } from '../SubscriptionFieldsPage/utils'

export default function SubscriptionsPage({
  areaId,
  number,
  stageId,
}: {
  areaId: string
  number: string | null
  stageId: string | null
}) {
  useAreaStyles(areaId)
  const reduxState = useSelector<RootState, RootState>(state => state)
  const { data } = getSubscriptionsByAreaId(reduxState, areaId)
  const { data: areaInfo } = getAreaInfoById(reduxState, areaId as string)

  return (
    <section className={styles.section}>
      <Container>
        <Title className={styles.section__title}>Выберите тариф</Title>
        <Paragraph className={styles.section__descr}>
          Абонемент начнет действовать после оплаты.
        </Paragraph>

        {data && data.length > 0 ? (
          <ul className={styles.list}>
            {data?.map(subscription => (
              <li key={subscription.id}>
                <Subscription
                  {...subscription}
                  areaId={areaId}
                  cardColor={areaInfo?.customising.cards_color}
                  number={number}
                  stageId={stageId}
                />
              </li>
            ))}
          </ul>
        ) : (
          <Card color="decor" cardColor={areaInfo?.customising.cards_color}>
            <div className={styles.notFound}>
              <div className={styles.notFound_Image}>
                <img src="/images/subscription-not-found.svg" alt="" />
              </div>
              <span>Нет доступных абонементов</span>
            </div>
          </Card>
        )}
      </Container>
    </section>
  )
}

interface SubscriptionProps extends IAreaSubscription {
  areaId: string
  number?: string | null
  stageId?: string | null
  cardColor?: string | null
}

export function Subscription({
  areaId,
  id,
  interval_type,
  interval,
  name,
  description,
  amount,
  balance,
  cardColor,
  available_count,
  type_work,
  fixed_period_from,
  fixed_period_to,
  week_days,
  number,
  stageId,
}: SubscriptionProps) {
  const disabled = balance < 1
  const price = `${formatNumberToString(+amount)}${unicode.no_break_space}${unicode.ruble}`

  const weekDays = week_days?.map(day => WeekDaysEnum[day]) ?? []
  const formatedWeekDays = formatWeekDays(weekDays)

  const subscriptionNumberHref = getLinkHref({
    type: 'subscription-number',
    data: { areaId, subscriptionId: id },
  })

  const subscriptionUserHref = getLinkHref({
    type: 'subscription-user',
    data: { areaId, subscriptionId: id, number, stageId },
  })

  const subscriptionInterval = getSubscriptionInterval({
    type_work,
    interval_type,
    interval,
    fixed_period_from,
    fixed_period_to,
  })

  return (
    <Card color="accent" className={styles.subscription} cardColor={cardColor}>
      <div className={styles.subscription_Content}>
        <header className={styles.subscription_Header}>
          <h2 className={styles.subscription_Title}>{name}</h2>
          <div className={styles.subscription_Header_data}>
            {formatedWeekDays && (
              <span className={styles.subscription_Interval}>
                <Icon name="calendar-icon" className={styles.subscription_Interval_icon} />
                <span className={styles.subscription_Interval_text}>{formatedWeekDays}</span>
              </span>
            )}
            <span className={styles.subscription_Interval}>
              <span className={styles.subscription_Interval_text}>{subscriptionInterval}</span>
            </span>
            <p className={styles.subscription_count}>Доступно: {available_count}</p>
          </div>
        </header>
        <p className={styles.subscription_Subtitle}>Что входит в абонемент:</p>
        {description && <p className={styles.subscription_Desc}>{description}</p>}

        {disabled ? (
          <Button disabled className={styles.subscription_Button} color="secondary" type="button">
            Нет мест
          </Button>
        ) : (
          <Link href={number ? subscriptionUserHref : subscriptionNumberHref} passHref>
            <Button component="a" className={styles.subscription_Button} color="secondary">
              {price}
            </Button>
          </Link>
        )}
      </div>
    </Card>
  )
}
